import {
  memo,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { COLUMN_WIDTH_MAX, COLUMN_WIDTH_MIN, COMPANY_FIELD_PREFIX, LEAD_SOURCE_SYSTEMS, type LeadRow, type LeadSortField, type LeadSource, type LeadSourceSystem } from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import { Link } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "../design/components/Button";
import { CheckboxInput } from "../design/components/CheckboxInput";
import { Chip } from "../design/components/Chip";
import { Dialog } from "../design/components/Dialog";
import { Icon } from "../design/components/Icon";
import { IconButton } from "../design/components/IconButton";
import { SortHeader } from "../design/components/SortHeader";
import { StatusPill } from "../design/components/StatusPill";
import { Table } from "../design/components/Table";
import { formatCount, formatNumericDateTime, localDayOf } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { formatEuro, formatNumber } from "./card/card-fields";
import { clampColumnWidth, SORT_WORDS, type ColumnDef, type LeadSort } from "./columns/table-layout";
import "./columns/columns.css";
import { countryName } from "./filters/filter-text";
import { formatPhoneForDisplay, leadName, leadSurname, outcomeLabel, statusTone } from "./lead-display";
import { usePrefetchLeadCard } from "./lead-queries";
import { bulkLeadName } from "./bulk/bulk-text";
import "./bulk/bulk.css";
import "./lead-list.css";

// La tabella dell'elenco Lead (piano M2 Task 17, mock-up `lead.html`, `COLUMNS` e `renderTable`): una tabella vera,
// con intestazioni e celle associate, a tutta larghezza; la prima colonna, «Cognome» (decisione del 6 ottobre 2026; prima
// era «Lead», nome e cognome insieme), ferma a sinistra e le intestazioni ferme in alto mentre la tabella scorre. Le righe
// sono virtuali (@tanstack/react-virtual): nel DOM ci sono soltanto quelle che si vedono, più un margine, e due righe vuote
// ne tengono il posto sopra e sotto; `aria-rowcount` e `aria-rowindex` dicono a chi usa uno screen reader quante sono e
// dove si trova. Una riga di una pagina non ancora arrivata si disegna vuota, della stessa altezza.
//
// Le colonne vengono dalla vista (piano M3 Task 6, D5): quelle visibili, nell'ordine della vista, con le larghezze
// scelte. Ogni intestazione si trascina con il puntatore (oltre i 6 px; «Cognome» resta prima), si allarga dal bordo
// destro, col puntatore o con le frecce, e ha il menu «…» con ordina, sposta a sinistra / a destra e nascondi: ogni
// azione del puntatore ha la sua strada da tastiera (UX_SYSTEM, «Configuratore Colonne»).
//
// «Chiamate» conta tutta la storia; «Chiamate negli ultimi 12 mesi» e «negli ultimi 4 mesi» (decisione del 1° ottobre
// 2026) stanno subito dopo. L'intestazione dice il nome breve, il suggerimento e la didascalia dell'ordinamento quello
// intero. I numeri stanno a destra, come le cifre di un foglio di calcolo.
//
// La prima colonna è l'intestazione della riga: il cognome con la sua iniziale, l'unico link alla scheda. Senza cognome mostra
// quello che mostrava «Lead» senza nome e cognome: il nome, oppure il salone, oppure il telefono (`leadName`), così ogni riga
// ha un link che si legge. Chi usa uno screen reader sente nel link nome e cognome insieme, il nome del Lead: il cognome da
// solo non distinguerebbe due Rossi. Il salone non sta più sotto il nome: ha la sua colonna, «Nome del salone».
//
// In una Smart List (piano M3 Task 9, mock-up `renderTable` con `position`) l'ordine delle righe è l'ordine di chiamata:
// ogni riga porta il suo numero d'ordine («Posto 1»), dentro la prima colonna, ferma a sinistra con il cognome. Con una sessione
// del Power Dialer da riprendere (piano M5 Task 6, mock-up `renderTable` con `worked`) i Lead già fatti portano il loro segno
// («Chiamato nella sessione da riprendere») sotto il cognome, dove prima stava il salone: la riga non cresce.
//
// Con le azioni multiple accese (piano M3 Task 14, `selection`; mock-up `renderTable` con `selection`) ogni riga ha la sua
// casella a sinistra del cognome, e la prima intestazione ha «Seleziona tutti i Lead mostrati», che spunta le righe arrivate.
// Con «Seleziona tutti i N risultati» ogni casella è spuntata e ferma: la scelta è la vista intera, non le righe.

export type { LeadSort } from "./columns/table-layout";

/** Righe da 42 px su desktop (UX_SYSTEM); su touch più alte, perché il nome resti un bersaglio di almeno 44 px. */
const ROW_HEIGHT = 42;
const TOUCH_ROW_HEIGHT = 52;
/** Le righe disegnate oltre quelle visibili, sopra e sotto: lo scorrimento veloce non mostra buchi. */
const OVERSCAN = 12;
/** Quanto si muove il puntatore prima che un clic sull'intestazione diventi un trascinamento. */
const DRAG_THRESHOLD = 6;
/** Il passo delle frecce sul bordo che allarga. */
const RESIZE_STEP = 8;

function useRowHeight(): number {
  const [height] = useState(() => (window.matchMedia("(pointer: coarse)").matches ? TOUCH_ROW_HEIGHT : ROW_HEIGHT));
  return height;
}

type Props = {
  caption: string;
  /** Le righe del perimetro filtrato: la tabella è alta come tutte, anche quelle non ancora caricate. */
  total: number;
  /** Falso mentre arriva la prima pagina: le righe vuote tengono il posto e il numero delle righe non si conosce. */
  totalKnown: boolean;
  /** La riga in quella posizione, se la sua pagina è arrivata. */
  rowAt: (index: number) => LeadRow | undefined;
  sort: LeadSort;
  /** Ordina per una colonna: senza direzione, prima dalla A alla Z e poi al contrario (clic sull'intestazione). */
  onSort: (field: LeadSortField, direction?: LeadSort["direction"]) => void;
  /** La prima e l'ultima riga disegnate (margine compreso): la pagina carica le pagine che le contengono. */
  onRangeChange: (first: number, last: number) => void;
  /** Cambia con ricerca, «Di chi», filtri e ordinamento: la tabella torna in cima. */
  resetKey: string;
  /** Righe di prima mostrate mentre arrivano quelle nuove. */
  busy: boolean;
  /** Il fuso dell'azienda della scheda: le date si leggono lì, mai nel fuso del browser. */
  timeZone: string;
  /** Le colonne visibili, nell'ordine della vista: «Cognome» per prima. */
  columns: readonly ColumnDef[];
  /** Le larghezze scelte; le altre colonne hanno quella di partenza. */
  widths: Readonly<Record<string, number>>;
  /** Sposta una colonna al posto `toIndex` fra le colonne visibili. */
  onMove: (fieldId: string, toIndex: number) => void;
  onResize: (fieldId: string, width: number) => void;
  onHide: (fieldId: string) => void;
  /** Il numero d'ordine di ogni riga (la posizione più uno): l'ordine di chiamata di una Smart List. */
  position?: boolean;
  /** Il segno sotto il cognome di alcune righe, per id del Lead (i Lead già fatti nella sessione da riprendere). */
  marks?: Readonly<Record<string, string>>;
  /** Le caselle delle azioni multiple, quando sono accese. */
  selection?: RowSelection;
};

type Drop = { id: string; side: "before" | "after" };

/** Le caselle delle azioni multiple (`useBulkSelection`). */
export type RowSelection = {
  /** Gli id spuntati, oppure `"all"`: tutti i risultati della vista. */
  picked: ReadonlySet<string> | "all";
  /** «Seleziona tutti i Lead mostrati»: le righe arrivate tutte spuntate, alcune, nessuna. */
  page: "none" | "some" | "all";
  /** Deve restare la stessa funzione fra un disegno e l'altro: le righe non si ridisegnano per questo. */
  onPick: (row: LeadRow, checked: boolean) => void;
  onPickPage: (checked: boolean) => void;
};

export function LeadTable({
  caption,
  total,
  totalKnown,
  rowAt,
  sort,
  onSort,
  onRangeChange,
  resetKey,
  busy,
  timeZone,
  columns,
  widths,
  onMove,
  onResize,
  onHide,
  position = false,
  marks,
  selection,
}: Props) {
  const t = useT();
  const rowHeight = useRowHeight();
  const scrollerRef = useRef<HTMLDivElement>(null);
  // Il progetto non usa il React Compiler: l'avviso sulla memoizzazione delle funzioni del virtualizzatore non lo riguarda.
  // eslint-disable-next-line react-hooks/incompatible-library -- nessuna memoizzazione automatica in questo progetto
  const virtualizer = useVirtualizer({
    count: total,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN,
  });
  const items = virtualizer.getVirtualItems();
  const firstItem = items[0];
  const lastItem = items.at(-1);
  const first = firstItem?.index ?? 0;
  const last = lastItem?.index ?? 0;

  useEffect(() => {
    onRangeChange(first, last);
  }, [first, last, onRangeChange]);

  // Una ricerca o un ordinamento nuovi ripartono dalla prima riga.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = 0;
  }, [resetKey]);

  // --- larghezze: quella scelta, o quella che si sta trascinando adesso ---
  const [liveWidth, setLiveWidth] = useState<{ id: string; width: number } | null>(null);
  const widthOf = (column: ColumnDef) => (liveWidth?.id === column.id ? liveWidth.width : (widths[column.id] ?? column.width));
  const tableWidth = columns.reduce((sum, column) => sum + widthOf(column), 0);

  // --- trascinamento delle intestazioni ---
  const headerRefs = useRef(new Map<string, HTMLTableCellElement>());
  const [dragging, setDragging] = useState<string | null>(null);
  const [drop, setDrop] = useState<Drop | null>(null);
  // Il clic che chiude un trascinamento non ordina; si dimentica subito dopo (anche quando il clic non arriva).
  const suppressClick = useRef(false);
  const stopPointer = useRef<(() => void) | null>(null);
  useEffect(() => () => stopPointer.current?.(), []);

  /** Dove cadrebbe la colonna con il puntatore a `x`: il posto fra le visibili (da 1, dopo «Cognome») e l'indicatore. */
  function dropSlot(x: number): { slot: number; drop: Drop } | null {
    for (let index = 1; index < columns.length; index += 1) {
      const column = columns[index];
      const cell = column ? headerRefs.current.get(column.id) : undefined;
      if (!column || !cell) continue;
      const box = cell.getBoundingClientRect();
      if (x < box.left + box.width / 2) return { slot: index, drop: { id: column.id, side: "before" } };
    }
    const lastColumn = columns.at(-1);
    return lastColumn && columns.length > 1 ? { slot: columns.length, drop: { id: lastColumn.id, side: "after" } } : null;
  }

  /**
   * Prende il puntatore sull'elemento, così anche un rilascio fuori dalla finestra chiude il gesto (dove il browser lo
   * sa fare: jsdom no).
   */
  function capturePointer(element: Element, pointerId: number) {
    try {
      element.setPointerCapture?.(pointerId);
    } catch {
      // Un puntatore già rilasciato non si prende: il gesto si chiude con il suo `pointerup`.
    }
  }

  /** Segue il puntatore fino al rilascio, anche fuori dalla tabella; `move` e `end` ricevono gli eventi di quel puntatore. */
  function followPointer(pointerId: number, move: (event: PointerEvent) => void, end: (event: PointerEvent) => void) {
    stopPointer.current?.();
    const handleMove = (event: PointerEvent) => {
      if (event.pointerId === pointerId) move(event);
    };
    const handleEnd = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;
      stop();
      end(event);
    };
    const stop = () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleEnd);
      window.removeEventListener("pointercancel", handleEnd);
      stopPointer.current = null;
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleEnd);
    window.addEventListener("pointercancel", handleEnd);
    stopPointer.current = stop;
  }

  function startDrag(column: ColumnDef, event: ReactPointerEvent<HTMLTableCellElement>) {
    suppressClick.current = false;
    // «Cognome» non si sposta; il bordo che allarga e il menu hanno i loro gesti.
    if (event.button !== 0 || column.always || (event.target instanceof Element && event.target.closest("[data-no-drag]"))) return;
    const from = columns.findIndex((candidate) => candidate.id === column.id);
    const cell = event.currentTarget;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    let active = false;
    let slot: number | null = null;
    followPointer(
      event.pointerId,
      (move) => {
        if (!active) {
          if (Math.abs(move.clientX - startX) < DRAG_THRESHOLD) return;
          active = true;
          setDragging(column.id);
          // Soltanto adesso: un clic semplice deve ancora arrivare al pulsante che ordina.
          capturePointer(cell, pointerId);
        }
        const target = dropSlot(move.clientX);
        slot = target?.slot ?? null;
        // Nessun indicatore dove la colonna resterebbe al suo posto. Lo stato cambia soltanto quando cambia il posto: un
        // movimento dentro la stessa metà di un'intestazione non ridisegna nulla.
        const next = target && target.slot !== from && target.slot !== from + 1 ? target.drop : null;
        setDrop((current) => (current?.id === next?.id && current?.side === next?.side ? current : next));
      },
      (end) => {
        if (!active) return;
        suppressClick.current = true;
        setTimeout(() => {
          suppressClick.current = false;
        }, 0);
        setDragging(null);
        setDrop(null);
        if (end.type !== "pointerup" || slot === null) return;
        const to = slot > from ? slot - 1 : slot;
        if (to !== from) onMove(column.id, to);
      },
    );
  }

  // --- il bordo destro che allarga ---
  function startResize(column: ColumnDef, event: ReactPointerEvent<HTMLSpanElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    capturePointer(event.currentTarget, event.pointerId);
    const start = widthOf(column);
    const startX = event.clientX;
    let width = start;
    followPointer(
      event.pointerId,
      (move) => {
        width = clampColumnWidth(start + move.clientX - startX);
        setLiveWidth((current) => (current?.id === column.id && current.width === width ? current : { id: column.id, width }));
      },
      (end) => {
        setLiveWidth(null);
        if (end.type === "pointerup" && width !== start) onResize(column.id, width);
      },
    );
  }

  function resizeWithKeys(column: ColumnDef, event: ReactKeyboardEvent<HTMLSpanElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    onResize(column.id, clampColumnWidth(widthOf(column) + (event.key === "ArrowRight" ? RESIZE_STEP : -RESIZE_STEP)));
  }

  // --- il menu «…» di una colonna ---
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuIndex = menuFor === null ? -1 : columns.findIndex((column) => column.id === menuFor);
  const menuColumn = columns[menuIndex];
  // Nascosta una colonna dal suo menu, il fuoco va al menu della colonna prima, che resta: dopo il disegno che segue.
  const focusMenuOf = useRef<string | null>(null);
  useEffect(() => {
    if (focusMenuOf.current === null) return;
    headerRefs.current.get(focusMenuOf.current)?.querySelector<HTMLButtonElement>("[data-column-menu]")?.focus();
    focusMenuOf.current = null;
  });

  const paddingTop = firstItem?.start ?? 0;
  const paddingBottom = lastItem ? virtualizer.getTotalSize() - lastItem.end : 0;

  return (
    <>
      <div ref={scrollerRef} className="g-lead-table-scroller" data-lead-scroller="">
        <Table
          className="g-lead-table"
          caption={caption}
          wrapped={false}
          // design-eccezione: la larghezza minima è la somma delle colonne scelte e allargate da chi usa la tabella
          style={{ minWidth: tableWidth }}
          aria-rowcount={totalKnown ? total + 1 : -1}
          busy={busy}
          data-dragging={dragging === null ? undefined : "true"}
        >
          <colgroup>
            {columns.map((column) => (
              // design-eccezione: la larghezza di ogni colonna la sceglie chi usa la tabella (bordo che allarga, ±8 px)
              <col key={column.id} style={{ width: widthOf(column) }} />
            ))}
          </colgroup>
          <thead>
            <tr aria-rowindex={1}>
              {columns.map((column) => (
                <HeaderCell
                  key={column.id}
                  column={column}
                  width={widthOf(column)}
                  sort={sort}
                  onSort={onSort}
                  cellRef={(cell) => {
                    if (cell) headerRefs.current.set(column.id, cell);
                    else headerRefs.current.delete(column.id);
                  }}
                  dragging={dragging === column.id}
                  drop={drop?.id === column.id ? drop.side : undefined}
                  onPointerDown={(event) => startDrag(column, event)}
                  onClickCapture={(event) => {
                    if (!suppressClick.current) return;
                    suppressClick.current = false;
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onResizeStart={(event) => startResize(column, event)}
                  onResizeKey={(event) => resizeWithKeys(column, event)}
                  onOpenMenu={() => setMenuFor(column.id)}
                  selection={column.always ? selection : undefined}
                />
              ))}
            </tr>
          </thead>
          {/* design-eccezione: l'altezza delle righe la misura la tabella virtualizzata (densità scelta e caratteri) */}
          <tbody style={{ "--g-lead-row-height": `${rowHeight}px` } as CSSProperties}>
            {paddingTop > 0 ? <Spacer height={paddingTop} span={columns.length} /> : null}
            {items.map((item) => {
              const row = rowAt(item.index);
              // La chiave è la posizione: con le pagine per `offset` lo stesso Lead può comparire due volte per un attimo,
              // se nel frattempo un altro Lead è entrato o uscito dall'elenco.
              return row ? (
                <LeadRowView
                  key={item.key}
                  row={row}
                  index={item.index}
                  timeZone={timeZone}
                  columns={columns}
                  position={position}
                  mark={marks?.[row.id] ?? null}
                  picking={selection !== undefined}
                  picked={selection !== undefined && (selection.picked === "all" || selection.picked.has(row.id))}
                  pickLocked={selection?.picked === "all"}
                  onPick={selection?.onPick}
                />
              ) : (
                <tr key={item.key} aria-rowindex={item.index + 2} className="g-lead-table__loading">
                  <th scope="row">
                    <span className="g-lead-table__skeleton" />
                    <span className="g-visually-hidden">{t("leadList.loadingRow")}</span>
                  </th>
                  {columns.slice(1).map((column) => (
                    <td key={column.id}>
                      <span className="g-lead-table__skeleton" />
                    </td>
                  ))}
                </tr>
              );
            })}
            {paddingBottom > 0 ? <Spacer height={paddingBottom} span={columns.length} /> : null}
          </tbody>
        </Table>
      </div>
      {menuColumn ? (
        <ColumnMenu
          column={menuColumn}
          sort={sort}
          canMoveLeft={menuIndex > 1}
          canMoveRight={menuIndex > 0 && menuIndex < columns.length - 1}
          onClose={() => setMenuFor(null)}
          onSort={(direction) => {
            setMenuFor(null);
            if (menuColumn.sort) onSort(menuColumn.sort.field, direction);
          }}
          onMove={(step) => {
            setMenuFor(null);
            onMove(menuColumn.id, menuIndex + step);
          }}
          onHide={() => {
            setMenuFor(null);
            focusMenuOf.current = columns[menuIndex - 1]?.id ?? null;
            onHide(menuColumn.id);
          }}
        />
      ) : null}
    </>
  );
}

/** Lo spazio delle righe non disegnate: tiene la tabella alta come tutte le righe, senza contare come una riga. */
function Spacer({ height, span }: { height: number; span: number }) {
  return (
    // design-eccezione: l'altezza delle righe non disegnate la calcola la virtualizzazione
    <tr role="presentation" className="g-lead-table__spacer" style={{ height }}>
      <td colSpan={span} />
    </tr>
  );
}

type HeaderProps = {
  column: ColumnDef;
  width: number;
  sort: LeadSort;
  onSort: (field: LeadSortField) => void;
  cellRef: (cell: HTMLTableCellElement | null) => void;
  dragging: boolean;
  drop: Drop["side"] | undefined;
  onPointerDown: (event: ReactPointerEvent<HTMLTableCellElement>) => void;
  onClickCapture: (event: ReactMouseEvent<HTMLTableCellElement>) => void;
  onResizeStart: (event: ReactPointerEvent<HTMLSpanElement>) => void;
  onResizeKey: (event: ReactKeyboardEvent<HTMLSpanElement>) => void;
  onOpenMenu: () => void;
  /** Soltanto nella prima intestazione («Cognome»), con le azioni multiple accese: «Seleziona tutti i Lead mostrati». */
  selection: RowSelection | undefined;
};

function HeaderCell({ column, width, sort, onSort, cellRef, dragging, drop, onPointerDown, onClickCapture, onResizeStart, onResizeKey, onOpenMenu, selection }: HeaderProps) {
  const t = useT();
  const labelId = useId();
  const sortable = column.sort;
  const active = sortable !== undefined && sortable.field === sort.field;
  const ariaSort = active ? (sort.direction === "asc" ? "ascending" : "descending") : undefined;
  // Il nome intero come suggerimento (e descrizione per chi usa uno screen reader); il nome resta quello che si vede.
  const title = column.fullLabel;
  const content = (
    <>
      <Icon name={column.icon} className="g-lead-table__th-icon" />
      <span id={labelId}>{column.label}</span>
    </>
  );
  return (
    // Il nome dell'intestazione è soltanto quello della colonna: il menu «…» e il bordo che allarga non si ripetono
    // a ogni cella letta da uno screen reader.
    <th
      ref={cellRef}
      scope="col"
      aria-labelledby={labelId}
      aria-sort={ariaSort}
      className={column.numeric ? "g-lead-table__number" : undefined}
      data-movable={column.always ? undefined : "true"}
      data-dragging={dragging ? "true" : undefined}
      data-drop={drop}
      onPointerDown={onPointerDown}
      onClickCapture={onClickCapture}
    >
      <span className="g-lead-table__th">
        {selection ? (
          <CheckboxInput
            className="g-bulk-check"
            label={t("bulkActions.selectPage")}
            data-no-drag=""
            checked={selection.page === "all"}
            // A metà quando soltanto alcune righe arrivate sono spuntate; un clic le spunta tutte.
            indeterminate={selection.page === "some"}
            disabled={selection.picked === "all"}
            onChange={selection.onPickPage}
          />
        ) : null}
        {sortable ? (
          // Un clic ordina: prima dalla A alla Z (o dal più basso, dal meno recente), poi al contrario (mock-up).
          <SortHeader className="g-lead-table__sort" title={title} label={content} direction={active ? sort.direction : null} onClick={() => onSort(sortable.field)} />
        ) : (
          // Una colonna che non si ordina non riceve il focus come se fosse un controllo (UX_SYSTEM, «Semantica»).
          <span className="g-lead-table__th-static" title={title}>
            {content}
          </span>
        )}
        <IconButton
          className="g-lead-table__menu"
          icon="more"
          label={t("tableView.menu")}
          aria-haspopup="dialog"
          title={t("tableView.menu")}
          data-column-menu=""
          data-no-drag=""
          onClick={onOpenMenu}
        />
      </span>
      {/* Il bordo destro allarga e stringe: col puntatore o con le frecce sinistra e destra (±8 px). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- un separatore che riceve il focus è un controllo (ARIA 1.2, «separator» con aria-valuenow) */}
      <span
        role="separator"
        aria-orientation="vertical"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- come sopra: il bordo si allarga anche da tastiera
        tabIndex={0}
        className="g-lead-table__resize"
        aria-label={t("tableView.resizeHandle", { column: column.label })}
        title={t("tableView.width")}
        aria-valuenow={width}
        aria-valuemin={COLUMN_WIDTH_MIN}
        aria-valuemax={COLUMN_WIDTH_MAX}
        data-no-drag=""
        onPointerDown={onResizeStart}
        onKeyDown={onResizeKey}
      />
    </th>
  );
}

type MenuProps = {
  column: ColumnDef;
  sort: LeadSort;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onClose: () => void;
  onSort: (direction: LeadSort["direction"]) => void;
  onMove: (step: -1 | 1) => void;
  onHide: () => void;
};

/** Il menu «…» di una colonna: soltanto azioni vere (UX_SYSTEM): ordina, sposta a sinistra / a destra, nascondi. */
function ColumnMenu({ column, sort, canMoveLeft, canMoveRight, onClose, onSort, onMove, onHide }: MenuProps) {
  const t = useT();
  const sortable = column.sort;
  const current = sortable && sortable.field === sort.field ? sort.direction : null;
  return (
    <Dialog open title={column.fullLabel ?? column.label} onClose={onClose}>
      <div className="g-column-menu">
        {sortable ? (
          <>
            <Button variant="ghost" aria-pressed={current === "asc"} onClick={() => onSort("asc")}>
              <Icon name="up" />
              {t("tableView.sortAsc", { direction: t(SORT_WORDS[sortable.kind].asc) })}
            </Button>
            <Button variant="ghost" aria-pressed={current === "desc"} onClick={() => onSort("desc")}>
              <Icon name="down" />
              {t("tableView.sortAsc", { direction: t(SORT_WORDS[sortable.kind].desc) })}
            </Button>
          </>
        ) : null}
        {column.always ? null : (
          <>
            <Button variant="ghost" disabled={!canMoveLeft} onClick={() => onMove(-1)}>
              <Icon name="left" />
              {t("tableView.moveLeft")}
            </Button>
            <Button variant="ghost" disabled={!canMoveRight} onClick={() => onMove(1)}>
              <Icon name="right" />
              {t("tableView.moveRight")}
            </Button>
            <Button variant="ghost" onClick={onHide}>
              <Icon name="minus" />
              {t("tableView.hide")}
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
}

type CellContext = { t: Translate; locale: Locale; timeZone: string };

/** La prima lettera di un nome, maiuscola: l'iniziale accanto al cognome. */
function initialOf(text: string): string | null {
  return Array.from(text.trim())[0]?.toLocaleUpperCase() ?? null;
}

const NONE = <span className="g-lead-table__none">—</span>;

/** Il valore di un campo dell'azienda: il nome della voce, il numero, gli euro o il testo; «—» se manca. */
function companyCell(column: ColumnDef, row: LeadRow, locale: Locale): ReactNode {
  const field = column.companyField;
  const key = column.id.slice(COMPANY_FIELD_PREFIX.length);
  const value = row.custom?.[key];
  if (value === undefined || value === null || value === "") return NONE;
  if (field?.type === "choice") {
    const option = field.options.find((candidate) => candidate.id === value);
    return <span className="g-lead-table__text">{option?.label ?? String(value)}</span>;
  }
  if (field?.type === "number" || field?.type === "currency_eur") {
    const number = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(number)) return NONE;
    return field.type === "currency_eur" ? formatEuro(locale, number) : formatNumber(locale, number);
  }
  return <span className="g-lead-table__text">{String(value)}</span>;
}

// Le celle «Città», «Status» e «Ultima chiamata», le stesse anche nelle righe di «Le cose da fare» (piano M5, Task 18), che
// portano il Lead come l'elenco. Usano le classi di `lead-list.css`.

/** «Città»: il comune e sotto la provincia per esteso, con la sigla soltanto accanto (decisione confermata); «—» se manca. */
export function cityCell(t: Translate, city: LeadRow["city"]): ReactNode {
  return city ? (
    <>
      <span className="g-lead-table__text">{city.name}</span>
      <span className="g-table__sub">{t("leadList.province", { name: city.provinceName, abbreviation: city.provinceAbbreviation })}</span>
    </>
  ) : (
    NONE
  );
}

/** «Status»: la pillola con il tono del mock-up. */
export function statusCell(status: LeadRow["status"]): ReactNode {
  return (
    <span className="g-lead-table__status" data-tone={statusTone(status.name)}>
      {status.name}
    </span>
  );
}

/** «Ultima chiamata»: giorno e ora in numeri nel fuso dell'azienda, sotto l'esito e se era in entrata; «Mai chiamato» senza. */
export function latestCallCell(t: Translate, timeZone: string, latest: LeadRow["latestCall"]): ReactNode {
  if (!latest) return <span className="g-lead-table__none">{t("leadList.neverCalled")}</span>;
  const sub = [latest.outcome ? outcomeLabel(t, latest.outcome) : null, latest.direction === "inbound" ? t("leadList.inbound") : null].filter(Boolean).join(" · ");
  return (
    <>
      <span className="g-lead-table__text">{formatNumericDateTime(timeZone, latest.at)}</span>
      {sub ? <span className="g-table__sub">{sub}</span> : null}
    </>
  );
}

/**
 * «Prossima task» (piano M5, Task 20; decisione del 25 settembre 2026; mock-up `cell("prossima")`): il richiamo aperto, giorno e ora
 * in numeri nel fuso dell'azienda, in rosso con «scaduta» se l'ora è passata, in giallo se è oggi; sotto «richiamo concordato» o
 * «richiamo». «—» senza richiamo aperto (questione 337). L'ora passata si confronta con l'orologio di quando la riga si disegna.
 */
export function nextTaskCell(t: Translate, timeZone: string, next: LeadRow["nextTask"]): ReactNode {
  if (!next) return NONE;
  const now = Date.now();
  const overdue = Date.parse(next.at) < now;
  const today = localDayOf(timeZone, next.at) === localDayOf(timeZone, new Date(now).toISOString());
  const when = formatNumericDateTime(timeZone, next.at);
  return (
    <>
      <StatusPill tone={overdue ? "danger" : today ? "warning" : "neutral"} icon="clock">
        {overdue ? `${when} · ${t("tasks.overdue")}` : when}
      </StatusPill>
      <span className="g-table__sub">{t(next.agreed ? "leadList.nextTask.agreed" : "leadList.nextTask.callback")}</span>
    </>
  );
}

const isLeadSourceSystem = (system: string): system is LeadSourceSystem => (LEAD_SOURCE_SYSTEMS as readonly string[]).includes(system);

/** Un ingresso di «Provenienza» in parole: la sorgente (dai cataloghi; una sconosciuta è «Altro ingresso») e la campagna, se c'è. */
function sourceText(t: Translate, source: LeadSource): string {
  const name = isLeadSourceSystem(source.system) ? t(`leadList.source.${source.system}`) : t("leadList.source.other");
  return source.campaign ? t("leadList.source.withCampaign", { source: name, campaign: source.campaign }) : name;
}

/**
 * «Provenienza» (LEAD-13, INGR-5): la prima attribuzione, «Moduli di Meta · Autunno saloni», e sotto «ultima: …» se l'ultimo
 * ingresso è un altro; «—» senza provenienza, anche finché la riga non la porta (la colonna appena accesa).
 */
function sourceCell(t: Translate, source: LeadRow["source"]): ReactNode {
  if (!source) return NONE;
  return (
    <>
      <span className="g-lead-table__text">{sourceText(t, source.first)}</span>
      {source.latest ? <span className="g-table__sub">{t("leadList.source.latest", { source: sourceText(t, source.latest) })}</span> : null}
    </>
  );
}

/** Le liste che «In Smart List» mostra prima di «+N». */
const SMART_LISTS_SHOWN = 3;

/**
 * «In Smart List» (LEAD-13): le liste come i tag, già in ordine alfabetico, al più tre e poi «+N», con i nomi delle altre nel
 * suggerimento e per chi usa uno screen reader; «—» senza liste.
 */
function smartListsCell(t: Translate, lists: LeadRow["smartLists"]): ReactNode {
  if (!lists || lists.length === 0) return NONE;
  const rest = lists.slice(SMART_LISTS_SHOWN);
  const names = rest.map((list) => list.name).join(", ");
  return (
    <span className="g-lead-table__tags">
      {lists.slice(0, SMART_LISTS_SHOWN).map((list) => (
        <Chip key={list.id}>{list.name}</Chip>
      ))}
      {rest.length > 0 ? (
        <Chip>
          <span aria-hidden="true" title={names}>
            {t("leadList.smartListsMore", { count: rest.length })}
          </span>
          <span className="g-visually-hidden">{t("leadList.smartListsMoreLabel", { count: rest.length, names })}</span>
        </Chip>
      ) : null}
    </span>
  );
}

/** La cella di una colonna per una riga (le colonne di sistema e i campi dell'azienda). */
function cellOf(column: ColumnDef, row: LeadRow, { t, locale, timeZone }: CellContext): ReactNode {
  switch (column.id) {
    case "city":
      return cityCell(t, row.city);
    case "status":
      return statusCell(row.status);
    case "latestCall":
      return latestCallCell(t, timeZone, row.latestCall);
    case "nextTask":
      return nextTaskCell(t, timeZone, row.nextTask);
    case "calls":
      return formatCount(locale, row.outgoingCallCount);
    case "calls12Months":
      return formatCount(locale, row.outgoingCallCountLast12Months);
    case "calls4Months":
      return formatCount(locale, row.outgoingCallCountLast4Months);
    case "tags":
      return row.tags.length > 0 ? (
        <span className="g-lead-table__tags">
          {row.tags.map((tag) => (
            <Chip key={tag.id}>{tag.name}</Chip>
          ))}
        </span>
      ) : (
        NONE
      );
    case "createdAt":
      return formatNumericDateTime(timeZone, row.createdAt);
    case "owner":
      return row.owner ? (
        <>
          <span className="g-lead-table__text">{row.owner.displayName}</span>
          {row.owner.active ? null : <span className="g-table__sub g-lead-table__warning">{t("leadList.ownerInactive")}</span>}
        </>
      ) : (
        <span className="g-lead-table__none">{t("leadList.noOwner")}</span>
      );
    case "firstName":
      return row.firstName ? <span className="g-lead-table__text">{row.firstName}</span> : NONE;
    case "salonName":
      return row.salonName ? <span className="g-lead-table__text">{row.salonName}</span> : NONE;
    case "mobile":
      return row.mobile ? <span className="g-lead-table__text">{formatPhoneForDisplay(row.mobile)}</span> : NONE;
    case "landline":
      return row.landline ? <span className="g-lead-table__text">{formatPhoneForDisplay(row.landline)}</span> : NONE;
    case "email":
      return row.email ? <span className="g-lead-table__text">{row.email}</span> : NONE;
    case "province":
      return row.city ? (
        <span className="g-lead-table__text">{t("leadList.province", { name: row.city.provinceName, abbreviation: row.city.provinceAbbreviation })}</span>
      ) : (
        NONE
      );
    case "region":
      return row.city ? <span className="g-lead-table__text">{row.city.regionName}</span> : NONE;
    case "country":
      return row.countryCode ? <span className="g-lead-table__text">{countryName(t, row.countryCode)}</span> : NONE;
    case "latestCallOutcome":
      if (!row.latestCall) return <span className="g-lead-table__none">{t("leadList.neverCalled")}</span>;
      return row.latestCall.outcome ? <span className="g-lead-table__text">{outcomeLabel(t, row.latestCall.outcome)}</span> : NONE;
    case "source":
      return sourceCell(t, row.source);
    case "smartLists":
      return smartListsCell(t, row.smartLists);
    case "lastName":
      // La prima colonna è l'intestazione della riga, disegnata da `LeadRowView`.
      return null;
    default:
      return companyCell(column, row, locale);
  }
}

/**
 * Una riga: ridisegnata soltanto quando cambiano i suoi dati o le colonne, non mentre si trascina un'intestazione o il
 * suo bordo (la tabella si ridisegna a ogni movimento del puntatore; le righe visibili no).
 */
type RowProps = {
  row: LeadRow;
  index: number;
  timeZone: string;
  columns: readonly ColumnDef[];
  position: boolean;
  /** Il segno sotto il cognome (`marks`), oppure `null`. */
  mark: string | null;
  /** Le azioni multiple sono accese: la riga ha la sua casella. */
  picking: boolean;
  picked: boolean;
  /** «Tutti i risultati»: la casella è spuntata e ferma. */
  pickLocked: boolean;
  onPick: ((row: LeadRow, checked: boolean) => void) | undefined;
};

const LeadRowView = memo(function LeadRowView({ row, index, timeZone, columns, position, mark, picking, picked, pickLocked, onPick }: RowProps) {
  const t = useT();
  // Il puntatore o il focus sul nome leggono in anticipo la scheda: si apre subito (velocità, criterio di accettazione).
  const prefetchCard = usePrefetchLeadCard();
  const { locale } = useLocale();
  // Il nome del Lead (nome e cognome, oppure il salone o il telefono) e quello che la prima colonna mostra: il cognome, oppure,
  // senza, lo stesso nome del Lead.
  const name = leadName(row);
  const shown = leadSurname(row);
  // L'iniziale di quello che si mostra (il cognome, oppure il nome o il salone); un Lead che si legge dal telefono ha l'icona.
  const initial = initialOf(row.lastName ?? "") ?? initialOf(row.displayName ?? "");
  const context: CellContext = { t, locale, timeZone };
  const lead = (
    <>
      {/* Soltanto il cognome apre la scheda: il resto della riga non è un pulsante (UX_SYSTEM, «Semantica»). */}
      <Link
        to="/lead/$leadId"
        params={{ leadId: row.id }}
        className="g-lead-table__name"
        // Il nome intero per chi usa uno screen reader, quando si vede soltanto il cognome (che ci sta dentro, in fondo).
        aria-label={name !== null && name !== shown ? name : undefined}
        onMouseEnter={() => prefetchCard(row.id)}
        onFocus={() => prefetchCard(row.id)}
        onTouchStart={() => prefetchCard(row.id)}
      >
        <span className="g-lead-table__avatar" aria-hidden="true">
          {initial ?? <Icon name="phone" />}
        </span>
        <span className="g-lead-table__text">{shown ?? "—"}</span>
      </Link>
      {mark !== null ? (
        <span className="g-table__sub g-lead-table__mark">
          <Icon name="check" />
          <span className="g-lead-table__mark-text">{mark}</span>
        </span>
      ) : null}
    </>
  );
  const cell = position ? (
    // Il numero d'ordine a sinistra del cognome; chi usa uno screen reader sente «Posto 1».
    <span className="g-lead-table__ordered">
      <span className="g-lead-table__position" title={t("smartLists.page.position", { position: index + 1 })}>
        <span className="g-visually-hidden">{t("smartLists.page.positionPrefix")} </span>
        {index + 1}
      </span>
      <span className="g-lead-table__ordered-lead">{lead}</span>
    </span>
  ) : (
    lead
  );
  return (
    <tr aria-rowindex={index + 2}>
      <th scope="row">
        {picking ? (
          // La casella a sinistra del cognome (mock-up), con il nome del Lead per chi usa uno screen reader.
          <span className="g-bulk-row">
            <CheckboxInput
              className="g-bulk-check"
              label={t("bulkActions.pickLead", { name: bulkLeadName(row) })}
              checked={picked}
              disabled={pickLocked}
              onChange={(checked) => onPick?.(row, checked)}
            />
            <span className="g-bulk-row__lead">{cell}</span>
          </span>
        ) : (
          cell
        )}
      </th>
      {columns.slice(1).map((column) => (
        <td key={column.id} className={column.numeric ? "g-lead-table__number" : undefined}>
          {cellOf(column, row, context)}
        </td>
      ))}
    </tr>
  );
});
